import { Config } from 'cms-types'
import QueryString from 'qs'

import { NextSearchParams } from '@/common/types'
import { logger } from '@/helpers/logger'

export const CMS_URL = process.env['PAYLOAD_INTERNAL_URL'] ?? process.env.NEXT_PUBLIC_PAYLOAD_URL

const CMS_FETCH_TIMEOUT_MS = Number(process.env['CMS_FETCH_TIMEOUT_MS'] ?? 30000)
const CMS_FETCH_RETRIES = Number(process.env['CMS_FETCH_RETRIES'] ?? 5)
const CMS_FETCH_CONCURRENCY = Number(process.env['CMS_FETCH_CONCURRENCY'] ?? 6)
const CMS_FETCH_BASE_DELAY_MS = 500
const CMS_FETCH_MAX_DELAY_MS = 4000

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const getBackoffDelay = (attempt: number) => {
  const expDelay = CMS_FETCH_BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1)
  const jitter = Math.floor(Math.random() * 200)
  return Math.min(CMS_FETCH_MAX_DELAY_MS, expDelay + jitter)
}

const isRetryableStatus = (status: number) => status === 408 || status === 429 || status >= 500

let cmsInFlight = 0
const cmsQueue: Array<() => void> = []

const acquireCmsSlot = () =>
  new Promise<void>(resolve => {
    if (cmsInFlight < CMS_FETCH_CONCURRENCY) {
      cmsInFlight += 1
      resolve()
      return
    }

    cmsQueue.push(() => {
      cmsInFlight += 1
      resolve()
    })
  })

const releaseCmsSlot = () => {
  cmsInFlight = Math.max(0, cmsInFlight - 1)
  const next = cmsQueue.shift()
  if (next) next()
}

export const fetchCmsJson = async <T>(
  url: string,
  options?: RequestInit,
  {
    retries = CMS_FETCH_RETRIES,
    timeoutMs = CMS_FETCH_TIMEOUT_MS,
    logLabel = 'CMS REQ',
  }: {
    retries?: number
    timeoutMs?: number
    logLabel?: string
  } = {},
): Promise<T> => {
  let lastError: unknown

  for (let attempt = 1; attempt <= retries; attempt += 1) {
    try {
      await acquireCmsSlot()
      const response = await fetch(url, {
        ...options,
        signal: AbortSignal.timeout(timeoutMs),
      })

      if (process.env['NODE_ENV'] === 'production') {
        logger.debug(logLabel, url)
      }

      if (!response.ok) {
        const bodyText = await response.text()
        const message = `CMS responded with ${response.status} ${response.statusText}`
        const error = new Error(`${message}: ${bodyText.slice(0, 500)}`)

        if (isRetryableStatus(response.status) && attempt < retries) {
          await sleep(getBackoffDelay(attempt))
          lastError = error
          continue
        }

        throw error
      }

      const bodyText = await response.text()
      try {
        return JSON.parse(bodyText) as T
      } catch (parseError) {
        throw new Error(
          `CMS responded with invalid JSON: ${bodyText.slice(0, 500)}`,
          parseError instanceof Error ? { cause: parseError } : undefined,
        )
      }
    } catch (error) {
      lastError = error

      if (attempt < retries) {
        await sleep(getBackoffDelay(attempt))
        continue
      }

      throw error
    } finally {
      releaseCmsSlot()
    }
  }

  throw lastError ?? new Error('CMS request failed')
}

export type Options = Partial<{
  preview: NextSearchParams['preview']
  locale: string
  depth: string
  limit: string
  type: string
}>

export const getCmsOptionsAsSearchParams = (options: Options = {}): string => {
  return QueryString.stringify(
    {
      draft: options.preview ? 'true' : 'false',
      locale: options.locale,
      depth: options.depth,
      limit: options.limit,
      'where[type][equals]': options.type,
    },
    {
      encodeValuesOnly: true,
    },
  )
}

export const errorWithCustomMessage = (error: unknown, type: string, url: string): Error => {
  return new Error(
    `Failed to fetch ${type} from CMS [${url}]: ${
      error instanceof Error ? error.message : String(error)
    }`,
    error instanceof Error ? { cause: error } : undefined,
  )
}

export const getCollection = async <T extends keyof Config['collections']>(
  collection: T,
  {
    searchParams = {},
    log = true,
  }: {
    searchParams?: Options
    log?: boolean
  } = {},
): Promise<Config['collections'][T][]> => {
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}`

  try {
    const { docs } = await fetchCmsJson<{ docs: Config['collections'][T][] }>(url, undefined, {
      logLabel: log ? 'CMS REQ COLLECTION' : 'CMS REQ',
    })

    return docs
  } catch (error) {
    throw errorWithCustomMessage(error, 'collection', url)
  }
}
